import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  Banner,
  BannerBar,
  Button,
  Card,
  Modal,
  MonoLabel,
  Pill,
  SectionHead,
  Skeleton,
  Spinner,
  Toast
} from '../components/primitives';
import { SettingsBackLink } from '../components/settings/SettingsBackLink';
import { TierCard, TIER_DISPLAY_NAME } from '../components/billing/TierCard';
import { SeatUsageBar } from '../components/billing/SeatUsageBar';
import { InvoiceList } from '../components/billing/InvoiceList';
import {
  BillingCheckoutModal,
  type CheckoutMode
} from '../components/billing/BillingCheckoutModal';
import { useBillingVM, type SubscriptionTier } from '../lib/vm/useBillingVM';
import { useCapability } from '../lib/vm/useCapability';
import { useActiveOrg as useShellActiveOrg } from '../lib/activeOrg';
import { supabase } from '../lib/supabase';
import type { DowngradeOverageMember } from '../components/billing/DowngradeOverageStep';
import './SettingsBilling.css';

const TIER_ORDER: ReadonlyArray<SubscriptionTier> = [
  'free',
  't25',
  't100',
  't200',
  't500'
];

function tierOrdinal(t: SubscriptionTier): number {
  return TIER_ORDER.indexOf(t);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  });
}

interface ActiveOrgState {
  status: 'loading' | 'ready' | 'unauth' | 'empty' | 'error';
  orgId: string | null;
  orgName: string | null;
  error: string | null;
}

/**
 * Resolves the caller's active org from the shell-owned active-org context
 * ({@link useShellActiveOrg}). The shell publishes the org the user actually
 * switched into; this surface is a pure reader. It no longer re-derives its
 * own "oldest membership" org — that was the cross-org leak where switching
 * orgs left billing pinned to the home org.
 *
 * We still call `getSession` once so the page can distinguish the `unauth`
 * banner from the loading state. While the shell hasn't published an org yet
 * (`activeOrgId === null`) we stay in `loading`; the effect re-runs and
 * resolves to `ready` the moment the shell settles or the user switches orgs.
 */
function useActiveOrg(): ActiveOrgState {
  const { activeOrgId, activeOrgName } = useShellActiveOrg();
  const [state, setState] = useState<ActiveOrgState>({
    status: 'loading',
    orgId: null,
    orgName: null,
    error: null
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!sessionData.session) {
        setState({
          status: 'unauth',
          orgId: null,
          orgName: null,
          error: null
        });
        return;
      }
      // No active org yet — the shell is still resolving which org to land in.
      // Stay in the loading state; this effect re-runs once it resolves.
      if (!activeOrgId) {
        setState({
          status: 'loading',
          orgId: null,
          orgName: null,
          error: null
        });
        return;
      }
      setState({
        status: 'ready',
        orgId: activeOrgId,
        orgName: activeOrgName,
        error: null
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrgId, activeOrgName]);

  return state;
}

interface OverageState {
  loading: boolean;
  members: ReadonlyArray<DowngradeOverageMember>;
}

/** Trimmed shape for the cancel-confirm modal's per-member list. We only need
 *  a stable display name; the picker semantics of `DowngradeOverageMember` are
 *  not needed since the cancel cascade is non-selective (all non-Owner active
 *  memberships suspend at period end). */
interface CancelCascadeMember {
  user_id: string;
  display_name: string;
}

interface CancelCascadeState {
  loading: boolean;
  members: ReadonlyArray<CancelCascadeMember>;
  /** True when the list fetch failed; the modal still renders with the
   *  generic copy and the cancel action remains functional — the list is
   *  informational only. */
  failed: boolean;
}

/**
 * One-shot fetch of non-Owner active members for the cancel-confirm modal's
 * informational list. Re-runs each time `enabled` flips to true so a user
 * re-opening the modal after closing it picks up any roster changes (e.g. a
 * member was removed in another tab). Errors swallow into `failed: true`,
 * leaving the cancel button functional with generic copy.
 */
function useCancelCascadeMembers(
  orgId: string | null,
  enabled: boolean
): CancelCascadeState {
  const [state, setState] = useState<CancelCascadeState>({
    loading: false,
    members: [],
    failed: false
  });

  useEffect(() => {
    if (!enabled || !orgId) {
      setState({ loading: false, members: [], failed: false });
      return;
    }
    let cancelled = false;
    setState({ loading: true, members: [], failed: false });
    (async () => {
      const { data, error } = await supabase.rpc('list_org_members', {
        p_org_id: orgId
      });
      if (cancelled) return;
      if (error) {
        setState({ loading: false, members: [], failed: true });
        return;
      }
      const raw = (data ?? []) as ReadonlyArray<{
        kind?: string | null;
        user_id?: string | null;
        display_name?: string | null;
        email?: string | null;
        permission_role_system_key?: string | null;
      }>;
      const out: CancelCascadeMember[] = [];
      for (const r of raw) {
        if (r.kind !== 'active') continue;
        if (r.permission_role_system_key === 'owner') continue;
        if (!r.user_id) continue;
        out.push({
          user_id: r.user_id,
          display_name: r.display_name ?? r.email ?? 'member'
        });
      }
      setState({ loading: false, members: out, failed: false });
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, enabled]);

  return state;
}

/**
 * Fetches non-Owner active members for the downgrade-overage picker. Lazy —
 * the parent only mounts this when the modal opens in `downgrade` mode and
 * `usedSeats > newMax`.
 */
function useOverageMembers(orgId: string | null, enabled: boolean): OverageState {
  const [state, setState] = useState<OverageState>({
    loading: false,
    members: []
  });

  useEffect(() => {
    if (!enabled || !orgId) {
      setState({ loading: false, members: [] });
      return;
    }
    let cancelled = false;
    setState({ loading: true, members: [] });
    (async () => {
      const { data, error } = await supabase.rpc('list_org_members', {
        p_org_id: orgId
      });
      if (cancelled) return;
      if (error) {
        setState({ loading: false, members: [] });
        return;
      }
      const raw = (data ?? []) as ReadonlyArray<{
        kind?: string | null;
        user_id?: string | null;
        display_name?: string | null;
        username?: string | null;
        discriminator?: number | null;
        permission_role_system_key?: string | null;
        permission_role_name?: string | null;
        function_role_label?: string | null;
      }>;
      const out: DowngradeOverageMember[] = [];
      for (const r of raw) {
        if (r.kind !== 'active') continue;
        if (r.permission_role_system_key === 'owner') continue;
        if (!r.user_id) continue;
        out.push({
          user_id: r.user_id,
          display_name: r.display_name ?? null,
          username: r.username ?? null,
          discriminator:
            typeof r.discriminator === 'number' ? r.discriminator : null,
          permission_role_system_key: r.permission_role_system_key ?? null,
          permission_role_name: r.permission_role_name ?? null,
          function_role_label: r.function_role_label ?? null
        });
      }
      setState({ loading: false, members: out });
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, enabled]);

  return state;
}

/**
 * `/settings/billing` — Owner/Admin-gated billing surface (turn 5B).
 *
 * Layout, top to bottom:
 *  1. Back link → `/settings`.
 *  2. Page title + subline.
 *  3. Conditional state Banner (scheduled-downgrade / scheduled-cancel /
 *     past-due / canceled).
 *  4. Current-plan section (tier name + price + status pill + seat usage).
 *  5. Pricing grid (5 TierCards).
 *  6. Payment method placeholder.
 *  7. Invoices (lazy).
 *  8. Footer: Cancel / Resume subscription.
 *
 * 3DS redirect-back: Stripe redirects to `/settings/billing?subscription_redirect=1&tier=…`;
 * the page reads the query params on mount and resumes `pollUntilActive`.
 */
export function SettingsBilling() {
  const orgState = useActiveOrg();
  const orgId = orgState.orgId;
  const capability = useCapability(orgId, 'billing.manage');
  const [searchParams, setSearchParams] = useSearchParams();

  const vm = useBillingVM(orgId);

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutTier, setCheckoutTier] = useState<SubscriptionTier>('t25');
  const [checkoutMode, setCheckoutMode] = useState<CheckoutMode>('new');
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  /** Defect 2 — keep the cancel modal mounted while the edge function is in
   *  flight so the user sees a spinner + "Canceling…" copy instead of a
   *  modal that disappears with no feedback. Cleared after the page state
   *  reflects the new `cancel_at_period_end=true` state. */
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [pollTimeoutRefresh, setPollTimeoutRefresh] = useState(false);

  // Resume the poll if Stripe redirected back from a 3DS challenge.
  useEffect(() => {
    if (!orgId) return;
    const redirected = searchParams.get('subscription_redirect') === '1';
    const redirectStatus = searchParams.get('redirect_status');
    if (!redirected) return;
    // Clear the query so a manual refresh doesn't re-poll.
    searchParams.delete('subscription_redirect');
    searchParams.delete('redirect_status');
    searchParams.delete('payment_intent');
    searchParams.delete('payment_intent_client_secret');
    searchParams.delete('tier');
    setSearchParams(searchParams, { replace: true });

    // Only poll if Stripe reports a positive redirect — `failed` / blank means
    // the user came back without completing.
    if (redirectStatus && redirectStatus !== 'succeeded') {
      setToastMsg("Payment didn't complete. Try again from your plan.");
      return;
    }
    (async () => {
      const r = await vm.pollUntilActive();
      if (r.ok) {
        setToastMsg('Subscription active.');
      } else {
        setPollTimeoutRefresh(true);
        setToastMsg(
          'Stripe is still processing. Refresh in a moment to see your plan.'
        );
      }
    })();
    // Run once on mount + when orgId settles. Adding vm/searchParams to deps
    // would loop — they're stable enough across the single redirect resume.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const overageEnabled =
    checkoutOpen &&
    checkoutMode === 'downgrade' &&
    vm.subscription !== null &&
    (() => {
      const cfg = vm.tierConfig.find((c) => c.tier === checkoutTier);
      if (!cfg) return false;
      return vm.seatUsage.usedSeats > cfg.max_seats;
    })();

  const overage = useOverageMembers(orgId, overageEnabled);

  // Cluster 5G: cancel-confirm modal informational list of non-Owner active
  // members who will be cascade-suspended at period end. Lazy — only fetches
  // while the modal is open. Failure leaves the list empty but keeps the
  // modal + cancel button functional.
  const cancelCascade = useCancelCascadeMembers(orgId, cancelConfirmOpen);

  const handleTierSelect = useCallback(
    (tier: SubscriptionTier) => {
      if (!vm.subscription) return;
      const current = vm.subscription.current_tier;
      const status = vm.subscription.status;
      const next = tierOrdinal(tier);
      const cur = tierOrdinal(current);
      // Free is reached via cancel, not via direct pick.
      if (tier === 'free') return;
      // Cluster 5K — Resume Checkout recovery. When the user's `current_tier`
      // is already set but `status` is `incomplete` (they closed the modal
      // mid-checkout), clicking the matching TierCard should re-open checkout
      // in `mode: 'new'` so `createSubscription` runs (which auto-cancels the
      // stale sub + mints a fresh PaymentIntent — `stripe-create-subscription`
      // 5H recovery branch).
      if (tier === current) {
        if (status === 'incomplete' || status === 'incomplete_expired') {
          setCheckoutTier(tier);
          setCheckoutMode('new');
          setCheckoutOpen(true);
        }
        return;
      }
      setCheckoutTier(tier);
      if (current === 'free') {
        setCheckoutMode('new');
      } else if (next > cur) {
        setCheckoutMode('upgrade');
      } else {
        setCheckoutMode('downgrade');
      }
      setCheckoutOpen(true);
    },
    [vm.subscription]
  );

  const handleUpdateCard = useCallback(() => {
    if (!vm.subscription) return;
    setCheckoutTier(vm.subscription.current_tier);
    setCheckoutMode('update-card');
    setCheckoutOpen(true);
  }, [vm.subscription]);

  const handleCheckoutSuccess = useCallback(async () => {
    setCheckoutOpen(false);
    await vm.refetchSubscription();
    await vm.seatUsage.refetch();
    setToastMsg('Plan updated.');
  }, [vm]);

  const handleCancel = useCallback(async () => {
    // Defect 2 — keep the modal open through the round-trip. The previous
    // implementation closed the modal synchronously and the page sat in a
    // stale state (no banner, no tier change) until the user manually
    // refreshed. We now: (1) stay open with the destructive button locked
    // into its "Canceling…" state via `vm.actionStatus === 'canceling'`
    // (already wired by the existing disabled+label logic below), (2) wait
    // for the edge function + `refetchSubscription` to land, (3) close the
    // modal only after the page state reflects the new cancel state.
    setCancelError(null);
    const result = await vm.cancelSubscription();
    if (!result.ok) {
      setCancelError(
        result.error ?? "Couldn't cancel your subscription. Please try again."
      );
      return;
    }
    setCancelConfirmOpen(false);
    setToastMsg(
      'Cancellation scheduled. Your plan stays active until the end of the current period.'
    );
  }, [vm]);

  const handleResume = useCallback(async () => {
    const result = await vm.resumeSubscription();
    if (result.ok) {
      setToastMsg('Subscription resumed.');
    }
  }, [vm]);

  const currentTier: SubscriptionTier =
    vm.subscription?.current_tier ?? 'free';
  const currentCfg = useMemo(
    () => vm.tierConfig.find((c) => c.tier === currentTier) ?? null,
    [vm.tierConfig, currentTier]
  );
  const checkoutCfg = useMemo(
    () => vm.tierConfig.find((c) => c.tier === checkoutTier) ?? null,
    [vm.tierConfig, checkoutTier]
  );

  const stateBanner = useStateBanner({
    vm,
    onResume: () => void handleResume(),
    onUpdateCard: handleUpdateCard,
    onPickPlan: () => {
      // Default the modal to the prior current tier so the user lands on a
      // sensible starting point.
      setCheckoutTier('t25');
      setCheckoutMode('new');
      setCheckoutOpen(true);
    }
  });

  // Capability check — gate the page on `billing.manage`. This early return
  // must sit AFTER every hook call above so hooks run unconditionally on
  // every render (react-hooks/rules-of-hooks). The gate condition is
  // unchanged — it short-circuits the render exactly as before.
  if (!capability.loading && orgId && !capability.allowed) {
    return <Navigate to="/settings" replace />;
  }

  const loading = vm.loading || capability.loading || orgState.status === 'loading';

  return (
    <section
      className="set-billing-page"
      data-testid="settings-billing"
      data-status={vm.state}
      data-current-tier={currentTier}
    >
      <SettingsBackLink />

      <header className="set-billing-header">
        <h1 className="set-billing-title">Billing</h1>
        <p className="set-billing-subline">Plan, seats, and invoices.</p>
      </header>

      {loading ? (
        <div className="set-billing-loading" data-testid="settings-billing-loading">
          <Skeleton height={48} />
          <Skeleton height={160} />
          <Skeleton height={120} />
        </div>
      ) : null}

      {orgState.status === 'unauth' ? (
        <Banner kind="default" data-testid="settings-billing-unauth">
          You need to sign in to view billing.
        </Banner>
      ) : null}

      {orgState.status === 'empty' ? (
        <Banner kind="default" data-testid="settings-billing-empty">
          You don't belong to any org yet.
        </Banner>
      ) : null}

      {orgState.status === 'error' ? (
        <Banner kind="error" data-testid="settings-billing-org-error">
          {orgState.error ?? "Couldn't load billing. Try again."}
        </Banner>
      ) : null}

      {vm.error ? (
        <Banner kind="error" data-testid="settings-billing-error">
          {vm.error}
        </Banner>
      ) : null}

      {!loading && orgState.status === 'ready' && vm.subscription && orgId ? (
        <>
          {/* Cluster 5K — Resume Checkout banner. The user opened checkout,
              closed the modal without paying, and returned to this page. The
              subscription row has `current_tier` set but `status='incomplete'`.
              `vm.state === 'processing'` in steady state means exactly this
              recovery shape; the banner explains the state so the user isn't
              confused about why the matching TierCard now reads
              `Resume checkout`. Suppressed while the checkout modal is open
              so a fresh in-flight attempt doesn't double-render the warning. */}
          {!checkoutOpen &&
          vm.state === 'processing' &&
          (vm.subscription.status === 'incomplete' ||
            vm.subscription.status === 'incomplete_expired') ? (
            <Banner
              kind="warn"
              data-testid="billing-banner-resume-checkout"
            >
              Your previous checkout didn't finish. Resume below.
            </Banner>
          ) : null}

          {stateBanner}

          {/* 1. Current plan */}
          <Card className="set-billing-card" data-testid="billing-current-plan">
            <SectionHead
              marker="01"
              title="Current plan"
              tag="ORG · BILLING"
            />
            <div className="set-billing-current-row">
              <div className="set-billing-current-stack">
                <h2 className="set-billing-current-name">
                  {TIER_DISPLAY_NAME[currentTier]}
                </h2>
                <div className="set-billing-current-price">
                  {currentCfg ? (
                    <>
                      <span className="set-billing-current-price-amount">
                        ${currentCfg.monthly_price_usd}
                      </span>
                      <MonoLabel size="sm" tracking="wide" tone="muted">
                        /mo
                      </MonoLabel>
                    </>
                  ) : null}
                </div>
                <div className="set-billing-current-meta">
                  <StatusPill state={vm.state} />
                  {currentTier !== 'free' &&
                  vm.subscription.current_period_end ? (
                    <MonoLabel size="sm" tracking="wide" tone="muted">
                      Renews {formatDate(vm.subscription.current_period_end)}
                    </MonoLabel>
                  ) : null}
                </div>
              </div>
              <div className="set-billing-current-seats">
                <SeatUsageBar
                  used={vm.seatUsage.usedSeats}
                  max={vm.seatUsage.maxSeats}
                  dataTestId="billing-seat-usage"
                />
              </div>
            </div>
            {currentTier === 'free' ? (
              <div className="set-billing-current-cta">
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => {
                    setCheckoutTier('t25');
                    setCheckoutMode('new');
                    setCheckoutOpen(true);
                  }}
                  data-testid="billing-choose-paid"
                >
                  Choose a paid plan
                </Button>
              </div>
            ) : null}
          </Card>

          {/* 2. Pricing grid */}
          <Card className="set-billing-card" data-testid="billing-pricing-grid">
            <SectionHead marker="02" title="Plans" tag="MONTHLY · USD" />
            <div className="set-billing-tier-grid">
              {vm.tierConfig.map((cfg) => {
                // Hide the Free row when the user is on a paid plan — Free is
                // reached via cancellation, not direct pick.
                if (cfg.tier === 'free' && currentTier !== 'free') {
                  return null;
                }
                return (
                  <TierCard
                    key={cfg.tier}
                    tier={cfg.tier}
                    price={cfg.monthly_price_usd}
                    maxSeats={cfg.max_seats}
                    currentTier={currentTier}
                    currentStatus={vm.subscription?.status ?? null}
                    busy={
                      vm.actionStatus !== 'idle' &&
                      vm.actionStatus !== 'polling'
                    }
                    disabled={
                      vm.state === 'canceled' || vm.state === 'past_due'
                    }
                    onSelect={handleTierSelect}
                  />
                );
              })}
            </div>
          </Card>

          {/* 3. Payment method (placeholder pending webhook-cached metadata). */}
          {currentTier !== 'free' ? (
            <Card
              className="set-billing-card"
              data-testid="billing-payment-method"
            >
              <SectionHead
                marker="03"
                title="Payment method"
                tag="CARD ON FILE"
              />
              <div className="set-billing-payment-row">
                <span className="set-billing-payment-summary">
                  {vm.subscription.default_payment_method_id
                    ? 'Card on file'
                    : 'No card on file yet'}
                </span>
                <Button
                  type="button"
                  variant="default"
                  onClick={handleUpdateCard}
                  data-testid="billing-update-card"
                >
                  Update
                </Button>
              </div>
              {/* TODO(billing-cache-pm-meta): the webhook will cache brand +
                  last4 on `subscriptions` so we can render "Visa ••4242"
                  instead of the generic placeholder above. See plan §2.3.6. */}
            </Card>
          ) : null}

          {/* 4. Invoices */}
          {currentTier !== 'free' ? (
            <Card className="set-billing-card" data-testid="billing-invoices-card">
              <SectionHead marker="04" title="Invoices" tag="LAST 10" />
              <InvoiceList orgId={orgId} limit={10} />
            </Card>
          ) : null}

          {/* 5. Footer — cancel / resume */}
          {currentTier !== 'free' && vm.state !== 'canceled' ? (
            <div className="set-billing-foot">
              {vm.subscription.cancel_at_period_end ? (
                <Button
                  type="button"
                  variant="default"
                  onClick={() => void handleResume()}
                  disabled={vm.actionStatus === 'resuming'}
                  data-testid="billing-resume-subscription"
                >
                  {vm.actionStatus === 'resuming'
                    ? 'Resuming…'
                    : 'Resume subscription'}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setCancelConfirmOpen(true)}
                  disabled={vm.actionStatus === 'canceling'}
                  data-testid="billing-cancel-subscription"
                >
                  Cancel subscription
                </Button>
              )}
            </div>
          ) : null}
        </>
      ) : null}

      {vm.actionError ? (
        <Banner kind="error" data-testid="settings-billing-action-error">
          {vm.actionError}
        </Banner>
      ) : null}

      {orgId && vm.subscription && checkoutCfg ? (
        <BillingCheckoutModal
          open={checkoutOpen}
          orgId={orgId}
          targetTier={checkoutTier}
          currentTier={currentTier}
          targetPrice={checkoutCfg.monthly_price_usd}
          targetMaxSeats={checkoutCfg.max_seats}
          usedSeats={vm.seatUsage.usedSeats}
          mode={checkoutMode}
          overageMembers={overage.members}
          overageMembersLoading={overage.loading}
          onClose={() => {
            setCheckoutOpen(false);
            // The `createSubscription` flow keeps `actionStatus` at `'creating'`
            // until either the user pays (→ poll → idle) OR confirms via
            // `onSuccess`. If the user closes the modal mid-checkout, reset so
            // the tier grid CTAs aren't stuck disabled (`busy={true}`).
            vm.resetAction();
          }}
          onSuccess={() => void handleCheckoutSuccess()}
          createSubscription={vm.createSubscription}
          previewTierChange={vm.previewTierChange}
          updateTier={vm.updateTier}
          pollUntilActive={vm.pollUntilActive}
          cancelSubscription={vm.cancelSubscription}
        />
      ) : null}

      <Modal
        open={cancelConfirmOpen}
        onClose={() => {
          // Defect 2 — don't allow dismissal while the cancel request is in
          // flight. The modal stays mounted, shows a spinner, and only
          // closes when `handleCancel` finishes successfully (which calls
          // `setCancelConfirmOpen(false)` itself).
          if (vm.actionStatus === 'canceling') return;
          setCancelConfirmOpen(false);
          setCancelError(null);
        }}
        title="Cancel subscription?"
        dataTestId="billing-cancel-confirm"
        footer={
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (vm.actionStatus === 'canceling') return;
                setCancelConfirmOpen(false);
                setCancelError(null);
              }}
              disabled={vm.actionStatus === 'canceling'}
              data-testid="billing-cancel-confirm-back"
            >
              {cancelError ? 'Close' : 'Keep subscription'}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => void handleCancel()}
              disabled={vm.actionStatus === 'canceling'}
              data-testid="billing-cancel-confirm-confirm"
            >
              {vm.actionStatus === 'canceling'
                ? 'Canceling…'
                : cancelError
                  ? 'Retry'
                  : 'Cancel subscription'}
            </Button>
          </>
        }
      >
        {vm.actionStatus === 'canceling' ? (
          <div
            className="set-billing-cancel-processing"
            data-testid="billing-cancel-confirm-processing"
          >
            <Spinner size="md" label="Canceling your subscription…" />
            <p className="set-billing-cancel-processing-text">
              Canceling your subscription…
            </p>
          </div>
        ) : null}
        {vm.actionStatus !== 'canceling' ? (
          <>
            <p data-testid="billing-cancel-confirm-line1">
              Your subscription will end on{' '}
              {formatDate(vm.subscription?.current_period_end ?? null)}. After
              that, your org returns to the Free plan (1 seat).
            </p>
            {vm.seatUsage.activeMembers > 1 &&
            !cancelCascade.failed &&
            !cancelCascade.loading &&
            cancelCascade.members.length > 0 ? (
              <>
                <p data-testid="billing-cancel-confirm-line2">
                  {cancelCascade.members.length} member
                  {cancelCascade.members.length === 1 ? '' : 's'} will lose
                  access at period end:
                </p>
                <ul
                  className="set-billing-cancel-cascade-list"
                  data-testid="billing-cancel-cascade-list"
                >
                  {cancelCascade.members.map((m) => (
                    <li
                      key={m.user_id}
                      data-testid="billing-cancel-cascade-row"
                    >
                      {m.display_name}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {cancelError ? (
              <Banner
                kind="error"
                data-testid="billing-cancel-confirm-error"
              >
                {cancelError}
              </Banner>
            ) : null}
          </>
        ) : null}
      </Modal>

      {toastMsg ? (
        <Toast onDismiss={() => setToastMsg(null)} title="Billing">
          {toastMsg}
          {pollTimeoutRefresh ? (
            <>
              {' '}
              <button
                type="button"
                className="set-billing-toast-action"
                onClick={() => {
                  if (typeof window !== 'undefined') window.location.reload();
                }}
                data-testid="billing-toast-refresh"
              >
                Refresh page
              </button>
            </>
          ) : null}
        </Toast>
      ) : null}

    </section>
  );
}

interface StatusPillProps {
  state: ReturnType<typeof useBillingVM>['state'];
}

function StatusPill({ state }: StatusPillProps) {
  if (state === 'active') return <Pill status="active">active</Pill>;
  if (state === 'past_due') return <Pill status="blocked">past due</Pill>;
  if (state === 'canceled') return <Pill status="cancel">canceled</Pill>;
  if (state === 'scheduled_cancel')
    return <Pill status="expired">ending soon</Pill>;
  if (state === 'scheduled_downgrade')
    return <Pill status="pending">downgrade scheduled</Pill>;
  if (state === 'processing')
    return <Pill status="progress">processing</Pill>;
  return <Pill status="draft">free</Pill>;
}

interface UseStateBannerArgs {
  vm: ReturnType<typeof useBillingVM>;
  onResume: () => void;
  onUpdateCard: () => void;
  onPickPlan: () => void;
}

/**
 * Computes the contextual state-banner JSX (or null) shown between the page
 * header and the Current-plan card. Drives the four states:
 *  - scheduled_downgrade → "Downgrading to {tier} on {date}". No Undo button
 *    in this cluster — the dedicated reverse action ships in a follow-up.
 *  - scheduled_cancel    → "Cancels on {date}". Resume button.
 *  - past_due            → "Payment failed. Update your payment method."
 *  - canceled            → Owner-only "Subscription canceled. Resubscribe to
 *    restore access." with a CTA that opens the pricing modal in `new` mode.
 */
function useStateBanner({
  vm,
  onResume,
  onUpdateCard,
  onPickPlan
}: UseStateBannerArgs): React.ReactNode {
  const sub = vm.subscription;
  if (!sub) return null;

  if (vm.state === 'scheduled_downgrade' && sub.scheduled_tier) {
    return (
      <Banner kind="warn" data-testid="billing-banner-scheduled-downgrade">
        Downgrading to {TIER_DISPLAY_NAME[sub.scheduled_tier]} on{' '}
        {formatDate(sub.scheduled_change_at)}.
        {/* TODO(billing-undo-downgrade): wire an Undo button once the
            `cancel_scheduled_downgrade` action ships. See plan §2.3 banner
            spec. The webhook will roll back `pending_suspension_user_ids`. */}
      </Banner>
    );
  }

  if (vm.state === 'scheduled_cancel') {
    return (
      <BannerBar
        kind="warn"
        data-testid="billing-banner-scheduled-cancel"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={onResume}
            disabled={vm.actionStatus === 'resuming'}
            data-testid="billing-banner-resume"
          >
            {vm.actionStatus === 'resuming' ? 'Resuming…' : 'Resume'}
          </Button>
        }
      >
        Subscription cancels on {formatDate(sub.current_period_end)}. After
        that date non-Owner members will be suspended.
      </BannerBar>
    );
  }

  if (vm.state === 'past_due') {
    return (
      <BannerBar
        kind="error"
        data-testid="billing-banner-past-due"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={onUpdateCard}
            data-testid="billing-banner-update-card"
          >
            Update payment method
          </Button>
        }
      >
        Payment failed. Update your payment method to keep your team's access.
      </BannerBar>
    );
  }

  if (vm.state === 'canceled') {
    return (
      <BannerBar
        kind="error"
        data-testid="billing-banner-canceled"
        actions={
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={onPickPlan}
            data-testid="billing-banner-resubscribe"
          >
            Choose a plan
          </Button>
        }
      >
        Subscription canceled. Resubscribe to restore access for suspended
        members.
      </BannerBar>
    );
  }

  return null;
}
